import classNames from "classnames";
import * as React from "react";

import { RobotoLink } from "@/shared/components/RobotoLink";
import type { AgentThreadGoalRecord } from "@/shared/domain/agentThread/AgentThreadRecord";
import {
  type EventsOutcome,
  type GoalOutcome,
  combinedOutcomeViewForGoal,
  createdEventsLink,
  paramsForGoal,
} from "@/shared/domain/agentThread/goalContent";
import { CheckIcon } from "@/shared/foundation/icons/mui";

import styles from "./GoalDetails.module.css";

interface GoalDetailsProps {
  goal: AgentThreadGoalRecord;
  /** Parsed outcome, or null while loading / when none is available. */
  outcome: GoalOutcome | null;
  /** Whether the outcome fetch is in flight. */
  isLoadingOutcome: boolean;
  /** Whether this goal can produce an outcome at all (drives the outcome row). */
  canHaveOutcome: boolean;
}

function ChipList({ items }: { items: string[] }) {
  return (
    <span className={styles.chips}>
      {items.map((item) => (
        <span key={item} className={styles.tag}>
          {item}
        </span>
      ))}
    </span>
  );
}

function Row({
  label,
  labelNote,
  children,
}: {
  label: string;
  /** Optional muted verdict shown next to the heading, e.g. "none selected". */
  labelNote?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={styles.row}>
      <span className={styles.rowLabel}>
        {label}
        {labelNote && (
          <span className={styles.rowLabelNote}>{` — ${labelNote}`}</span>
        )}
      </span>
      {children}
    </div>
  );
}

/** The declaration parameters worth showing per goal type (none for summary). */
function ParamsSection({
  goal,
  omitVocabulary = false,
}: {
  goal: AgentThreadGoalRecord;
  /** Skip the label/event-type chips, which a combined view renders itself. */
  omitVocabulary?: boolean;
}) {
  const params = paramsForGoal(goal);
  const rows: React.ReactNode[] = [];

  if (
    !omitVocabulary &&
    params.labelOptions &&
    params.labelOptions.length > 0
  ) {
    rows.push(
      <Row key="labels" label="Labels">
        <ChipList items={params.labelOptions} />
      </Row>,
    );
  }
  if (!omitVocabulary && params.eventTypes && params.eventTypes.length > 0) {
    rows.push(
      <Row key="event-types" label="Event types">
        <ChipList items={params.eventTypes} />
      </Row>,
    );
  }
  if (params.collectionId) {
    rows.push(
      <Row key="collection" label="Collection">
        <RobotoLink to={`/collections/${params.collectionId}`}>
          {params.collectionId}
        </RobotoLink>
      </Row>,
    );
  }

  return <>{rows}</>;
}

interface MarkedChip {
  name: string;
  /** Whether the agent used this entry (applied the label, created the type). */
  marked: boolean;
  /** Shown after the name on a marked chip, when known. */
  count?: number;
  ariaLabel: string;
}

/** A vocabulary as chips, with the entries the agent used lifted out in green
 *  and a leading check so the distinction does not rely on color alone. */
function MarkedChipList({ chips }: { chips: MarkedChip[] }) {
  return (
    <span className={styles.chips}>
      {chips.map(({ name, marked, count, ariaLabel }) => (
        <span
          key={name}
          className={classNames(styles.tag, {
            [styles.tagApplied]: marked,
          })}
          aria-label={marked ? ariaLabel : name}
        >
          {marked && <CheckIcon className={styles.tagAppliedIcon} />}
          {marked && count !== undefined ? `${name} \u00d7${count}` : name}
        </span>
      ))}
    </span>
  );
}

/**
 * The combined views' shared layout: the whole declared vocabulary as one chip
 * list with the used entries highlighted. The vocabulary comes from the goal
 * declaration (available immediately); which chips are highlighted comes from
 * the achieve-tool detail (fetched lazily when the row expands), so the chips
 * render neutral first and light up once the outcome resolves.
 */
function VocabularySection({
  label,
  labelNote,
  chips,
  emptyText,
  pendingText,
  outcomePending,
  isLoadingOutcome,
  children,
}: {
  label: string;
  labelNote?: string;
  chips: MarkedChip[];
  emptyText: string;
  /** Shown beneath the chips while the outcome is loading / unavailable. */
  pendingText: { loading: string; unavailable: string };
  outcomePending: boolean;
  isLoadingOutcome: boolean;
  children?: React.ReactNode;
}) {
  return (
    <>
      <Row label={label} labelNote={labelNote}>
        {chips.length > 0 ? (
          <MarkedChipList chips={chips} />
        ) : (
          // Suppressed while the outcome is loading: an empty list there would
          // read as a verdict rather than a gap.
          !outcomePending && <span className={styles.muted}>{emptyText}</span>
        )}
        {/* Shown beneath the neutral chips, not instead of them. */}
        {outcomePending && (
          <span className={styles.muted}>
            {isLoadingOutcome ? pendingText.loading : pendingText.unavailable}
          </span>
        )}
      </Row>
      {children}
    </>
  );
}

/** Triage: the candidate labels, with the applied ones highlighted. */
function TriageLabelsSection({
  goal,
  outcome,
  isLoadingOutcome,
  canHaveOutcome,
}: GoalDetailsProps) {
  const { labelOptions = [] } = paramsForGoal(goal);
  const appliedLabels = outcome?.kind === "triage" ? outcome.appliedLabels : [];
  const appliedSet = new Set(appliedLabels);
  // Union so an applied label that isn't in the declared vocabulary (shouldn't
  // happen, but the two come from different sources) still surfaces.
  const allLabels = Array.from(new Set([...labelOptions, ...appliedLabels]));

  // A resolved outcome that applied nothing renders all-neutral chips, the same
  // as the loading state, so the heading says "none selected" explicitly.
  const noneSelected =
    outcome !== null && appliedLabels.length === 0 && allLabels.length > 0;

  return (
    <VocabularySection
      label="Labels"
      labelNote={noneSelected ? "none selected" : undefined}
      chips={allLabels.map((name) => ({
        name,
        marked: appliedSet.has(name),
        ariaLabel: `${name} (applied)`,
      }))}
      // Empty only when the vocabulary itself was empty, so "No labels", not
      // "No labels applied" (which would imply options went unchosen).
      emptyText="No labels"
      pendingText={{
        loading: "Loading applied labels…",
        unavailable: "Applied labels unavailable",
      }}
      outcomePending={canHaveOutcome && outcome === null}
      isLoadingOutcome={isLoadingOutcome}
    />
  );
}

/**
 * Create-events: the declared event types, with the ones the agent created
 * highlighted (and counted, when the counts are reliable), then the target
 * collection and the overall result.
 */
function EventTypesSection({
  goal,
  outcome,
  isLoadingOutcome,
  canHaveOutcome,
}: GoalDetailsProps) {
  const { eventTypes = [] } = paramsForGoal(goal);
  const created =
    outcome?.kind === "events"
      ? outcome.createdByType
      : new Map<string, number | undefined>();
  const allTypes = Array.from(new Set([...eventTypes, ...created.keys()]));
  const outcomePending = canHaveOutcome && outcome === null;

  return (
    <>
      <VocabularySection
        label="Event types"
        chips={allTypes.map((name) => {
          const count = created.get(name);
          return {
            name,
            marked: created.has(name),
            count,
            ariaLabel:
              count === undefined
                ? `${name} (created)`
                : `${name} (${count} created)`,
          };
        })}
        emptyText="No event types"
        pendingText={{
          loading: "Loading created events…",
          unavailable: "Created events unavailable",
        }}
        outcomePending={outcomePending}
        isLoadingOutcome={isLoadingOutcome}
      />
      <ParamsSection goal={goal} omitVocabulary />
      {outcome && <OutcomeSection outcome={outcome} />}
    </>
  );
}

/** The derived outcome per goal type, once the achieve-tool detail has loaded. */
function OutcomeSection({ outcome }: { outcome: GoalOutcome }) {
  switch (outcome.kind) {
    case "triage":
      // Unreachable from ``GoalDetails``: triage renders through
      // ``TriageLabelsSection``, which shows the applied labels in context.
      return null;
    case "summary":
      return (
        <Row label="Summary">
          {outcome.summary ? (
            <span className={styles.summaryText}>{outcome.summary}</span>
          ) : (
            <span className={styles.muted}>No summary recorded</span>
          )}
        </Row>
      );
    case "events":
      return (
        <Row label="Result">
          <EventsResult outcome={outcome} />
        </Row>
      );
  }
}

/** The created-event count with a link to browse them, or a muted "none" note. */
function EventsResult({ outcome }: { outcome: EventsOutcome }) {
  const count = outcome.createdEventIds.length;
  if (count === 0) {
    return <span className={styles.muted}>No events created</span>;
  }
  const link = createdEventsLink(outcome);
  return (
    <span>
      {`${count} event${count === 1 ? "" : "s"} created`}
      {link && (
        <>
          {" — "}
          <RobotoLink to={link}>view events</RobotoLink>
        </>
      )}
    </span>
  );
}

/**
 * What a goal asked for and what came of it: the declaration parameters worth
 * surfacing per goal type (triage labels, create-events event types and target
 * collection) and, once the achieve-tool detail has loaded, the outcome (applied
 * labels, the persisted summary, or a link to the events created). The goal's
 * name and status are shown by the row this sits under, not repeated here.
 */
export function GoalDetails({
  goal,
  outcome,
  isLoadingOutcome,
  canHaveOutcome,
}: GoalDetailsProps) {
  const props = { goal, outcome, isLoadingOutcome, canHaveOutcome };
  // Triage and create-events fold their declared vocabulary and their outcome
  // into one highlighted chip list, so they bypass the generic params +
  // outcome rows.
  let content: React.ReactNode;
  switch (combinedOutcomeViewForGoal(goal.goal_type)) {
    case "triage":
      content = <TriageLabelsSection {...props} />;
      break;
    case "events":
      content = <EventTypesSection {...props} />;
      break;
    case undefined:
      content = (
        <>
          <ParamsSection goal={goal} />

          {canHaveOutcome && (
            <>
              <div className={styles.divider} />
              {outcome ? (
                <OutcomeSection outcome={outcome} />
              ) : (
                <span className={styles.muted}>
                  {isLoadingOutcome
                    ? "Loading outcome…"
                    : "Outcome unavailable"}
                </span>
              )}
            </>
          )}
        </>
      );
      break;
  }

  return <div className={styles.details}>{content}</div>;
}
